import { useEffect, useMemo } from 'react';
import { useForm, useFormContext, type UseFormReturn } from 'react-hook-form';

import { cleanDocument } from '../credit-note.list-actions';
import type { CreditNote, RelatedPerson, Upload } from '../credit-note.wire';

// Contrato das abas Garantias, Envolvidos e Documentos com o Salvar da operação (relatório das abas,
// "Contrato para o Salvar"). No legado os quatro campos são do formulário da operação
// (CreditNoteFormContainer.tsx:161-166, `defaultValues = GET`): os drawers só fazem `setValue` e o fio
// sai no Salvar (`editOrCreateCreditNote`, creditNote.ts:150-266) ou no "Salvar documentos".

/** Uma garantia como o drawer grava no formulário: os campos do tipo, depois do cast do yup e do `mapDataPrevFields`. */
export type WarrantyItem = Readonly<Record<string, unknown>> & { readonly warrantyType?: string | null | undefined };
/** Um envolvido como o drawer grava (`setNewRelatedPerson`, InvolvedTabContainer.tsx:186-225). */
export type InvolvedItem = RelatedPerson & Readonly<Record<string, unknown>>;
/** Um documento da lista (`uploads`): o do `GET` ou o que o drawer acabou de enviar (sem `id`). */
export type UploadItem = Upload & Readonly<Record<string, unknown>>;

/** Os campos que as abas editam. O formulário da operação que assumir o detalhe precisa ter todos. */
export interface CreditNoteTabsValues {
  warranty: WarrantyItem[];
  collateral: string | null;
  relatedPersonCreditNote: InvolvedItem[];
  uploads: UploadItem[];
  /** A garantia SIAPE de consignado público grava a margem em `amortization.requestedAmount` (WarrantFormContainer.tsx:89-91). */
  amortization: Readonly<Record<string, unknown>> & { requestedAmount?: number | null | undefined };
}

/** Os valores iniciais a partir do `GET /CreditNote/{id}` (o `defaultValues` do legado nesses campos). */
export function creditNoteTabsDefaults(note: CreditNote): CreditNoteTabsValues {
  return {
    warranty: (note.warranty ?? []).map((item) => ({ ...item })),
    collateral: note.collateral ?? null,
    relatedPersonCreditNote: (note.relatedPersonCreditNote ?? []).map((item) => ({ ...item })),
    uploads: (note.uploads ?? []).map((item) => ({ ...item })),
    amortization: { ...(note.amortization ?? {}) },
  };
}

const EMPTY_TABS: CreditNoteTabsValues = { warranty: [], collateral: null, relatedPersonCreditNote: [], uploads: [], amortization: {} };

/**
 * O formulário local das abas, enquanto o formulário da operação não assume o detalhe. O que ninguém mexeu
 * acompanha o `GET` (`keepDirtyValues`); a lista de documentos volta a ser a do servidor a cada leitura,
 * como o legado (UploadWithRelatedContainer.tsx:130-138).
 */
export function useCreditNoteTabsForm(note: CreditNote | undefined): UseFormReturn<CreditNoteTabsValues> {
  const values = useMemo(() => (note ? creditNoteTabsDefaults(note) : EMPTY_TABS), [note]);
  const form = useForm<CreditNoteTabsValues>({ defaultValues: values, values, resetOptions: { keepDirtyValues: true } });
  const { resetField } = form;
  useEffect(() => {
    resetField('uploads', { defaultValue: values.uploads });
  }, [values.uploads, resetField]);
  return form;
}

/** As abas leem e escrevem no formulário que o detalhe provê (`FormProvider`). */
export function useTabsForm() {
  return useFormContext<CreditNoteTabsValues>();
}

/** A operação com os valores do formulário por cima: a barra ("Salvar documentos") lê `note.uploads`, como o legado lê `watch('uploads')`. */
export function withTabsValues(note: CreditNote, values: Partial<CreditNoteTabsValues>): CreditNote {
  return {
    ...note,
    ...(values.warranty ? { warranty: values.warranty } : {}),
    ...(values.collateral === undefined ? {} : { collateral: values.collateral }),
    ...(values.relatedPersonCreditNote ? { relatedPersonCreditNote: values.relatedPersonCreditNote } : {}),
    ...(values.uploads ? { uploads: values.uploads } : {}),
  };
}

// ─── Salvar: o formato do PUT /CreditNote/{id} ──────────────────────────────

// Chaves dos modelos que o `toDataModel` mantém, na ordem das classes do legado (as classes do CRA
// inicializam todos os campos declarados; services/creditNote/types/WarrantyModel/*.ts).
const WARRANTY_MODELS: Readonly<Record<string, readonly string[]>> = {
  Vehicle: ['warrantyType', 'renavam', 'typeOfVehicle', 'chassis', 'board', 'supplier', 'taxNumberOfSupplier', 'brand', 'color', 'model', 'yearOfManufacture', 'yearOfModel', 'typeOfFuel', 'plateState', 'operationState', 'downPayment', 'sellerCompanyName', 'sellerRegistrationNumber', 'valueInCash', 'subtotalAmount', 'totalAmountFinancedTaxFree'],
  InvoiceFactoring: ['warrantyType', 'payerName', 'payerRegistrationNumber', 'contractNumber', 'invoiceNumber', 'invoiceValue', 'discountValue', 'presentValue', 'dueDate', 'paymentAmountInCents', 'paymentItemIndex', 'paymentItemTotal', 'yourNumber', 'storeCode', 'productCode', 'creditType', 'identification', 'emissionDate', 'index', 'payerDescription', 'payerAdress', 'payerZipCodeAdress', 'contractFee'],
  HomeEquity: ['warrantyType', 'registryOffice', 'registrationNumber', 'width', 'length', 'addressName', 'zipCodeAddress', 'city', 'uf', 'district', 'addressNumber', 'complementAddress', 'propertyFeatures'],
  JudiciaryProcess: ['warrantyType', 'number', 'court', 'valueOfCause', 'claimant', 'claimed', 'typeOfProcess'],
  Factoring: ['warrantyType', 'totalValue', 'register', 'type', 'splitMethodType', 'paymentNetworks', 'constituted'],
  DataprevEmployee: ['warrantyType', 'dataprev_EmployerName', 'dataprev_EmployeeCode', 'dataprev_EmployerRegistrationCode', 'dataprev_EmployerRegistrationNumber', 'dataprev_EmployeeAdmissionDate', 'dataprev_DiscountStartPeriod', 'totalValue', 'dataprev_HasWarranty', 'dataprev_WarrantyFgtsBalance', 'dataprev_WarrantyFgtsRescissionPenalty', 'dataprev_WarrantyRescissionBenefitPercentage'],
  PublicPayroll: ['warrantyType', 'employeeCode', 'agreementCode', 'employeerInstCode', 'payrollAgreementId', 'governmentDepartmentCode', 'payrollAgreementName', 'warrantyCode', 'paymentTransferDay', 'paymentAmountInCents', 'employmentStatusCode', 'employmentStatusDescription', 'previousAdeNumber', 'warrantyDeadline', 'warrantyOption', 'warrantyOptionDisplay', 'simulationRegistrationNumber', 'termInMonths', 'governmentDepartmentWarrantyStatusDisplay', 'payingSourceName', 'payingSourceRegistrationNumber'],
  Consortium: ['warrantyType', 'administratorName', 'administratorRegistrationNumber', 'quota', 'group'],
};
/** Documento que o serviço limpa antes do fio (creditNote.ts:197-236). */
const CLEANED: Readonly<Record<string, readonly string[]>> = {
  Vehicle: ['sellerRegistrationNumber', 'taxNumberOfSupplier'],
  InvoiceFactoring: ['payerRegistrationNumber'],
  DataprevEmployee: ['dataprev_EmployerRegistrationNumber'],
};

/**
 * Uma garantia → o item do `PUT /CreditNote/{id}` (creditNote.ts:192-247): os tipos com modelo passam pelo
 * `toDataModel` (só as chaves da classe que existem no item); Carga, Car Equity, Refinanciamento e INSS vão
 * como estão. O `warrantyType` é comparado com a caixa do legado (`=== 'Vehicle'`).
 */
export function warrantyToWire(item: WarrantyItem, options: { readonly alphanumeric?: boolean } = {}): Record<string, unknown> {
  const type = typeof item.warrantyType === 'string' ? item.warrantyType : '';
  const keys = WARRANTY_MODELS[type];
  if (!keys) return { ...item };
  const out: Record<string, unknown> = {};
  for (const key of keys) if (key in item) out[key] = item[key];
  for (const key of CLEANED[type] ?? []) {
    const value = out[key];
    // O Dataprev devolve o CNPJ do empregador em número; o legado faz `.toString()` antes de limpar (creditNote.ts:227-232).
    if ((typeof value === 'string' && value !== '') || (typeof value === 'number' && value !== 0)) out[key] = cleanDocument(String(value), options.alphanumeric ?? true);
  }
  return out;
}

/** Os campos das abas no formato do `PUT /CreditNote/{id}` do legado. `amortization` é do formulário da operação. */
export function tabsToWire(values: CreditNoteTabsValues, options: { readonly alphanumeric?: boolean } = {}) {
  return {
    warranty: values.warranty.map((item) => warrantyToWire(item, options)),
    collateral: values.collateral,
    relatedPersonCreditNote: values.relatedPersonCreditNote,
    uploads: values.uploads,
  };
}
